import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMonitor } from '../../api/monitor.js';
import type { MonitorProject } from '../../api/types.js';
import { KEEP_ALIVE_EVENT } from '../../auth/guards.js';
import { Alert, Button, Card, Spinner } from '../../components/ui.js';
import { BuildStatusBadge, DeployStatusBadge, ProjectStatusBadge } from '../../status.js';

/** Sort: things needing attention first, then projects with builds in flight. */
function priority(p: MonitorProject): number {
  if (p.attention.needsApproval || p.attention.unreadMessages || p.failed > 0) return 0;
  if (p.active.length > 0) return 1;
  return 2;
}

/**
 * The staff monitor: a live, auto-refreshing view of every active project — what
 * is building, what has failed, deploy state — with anything needing attention
 * (a build awaiting approval, unread founder messages, recent failures)
 * highlighted and floated to the top. Built to leave open on a wall or tablet.
 */
export function MonitorPage() {
  const [auto, setAuto] = useState(true);
  const monitor = useMonitor(auto);

  // While auto-refreshing, treat each successful poll as activity so an
  // unattended wall/tablet display is not idle-logged-out — the polling keeps the
  // server session alive, and this keeps the client idle timer in step. With
  // auto-refresh off, normal idle logout still applies.
  useEffect(() => {
    if (auto && monitor.dataUpdatedAt) {
      window.dispatchEvent(new Event(KEEP_ALIVE_EVENT));
    }
  }, [auto, monitor.dataUpdatedAt]);

  const projects = monitor.data
    ? [...monitor.data.projects].sort(
        (a, b) => priority(a) - priority(b) || b.active.length - a.active.length,
      )
    : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-title text-3xl tracking-wide">Monitor</h1>
        <span className="text-sm opacity-60">
          {monitor.isFetching ? 'Refreshing…' : 'Live view of every active project'}
        </span>
        <label className="ml-auto flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={auto}
            onChange={(e) => setAuto(e.target.checked)}
          />
          Auto-refresh
        </label>
        <Button
          variant="secondary"
          disabled={monitor.isFetching}
          onClick={() => void monitor.refetch()}
        >
          Refresh now
        </Button>
      </div>

      {monitor.isPending ? (
        <Card>
          <Spinner />
        </Card>
      ) : monitor.isError || !monitor.data ? (
        <Card>
          <Alert>Could not load the monitor.</Alert>
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <p className="opacity-70">No active projects.</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <MonitorCard key={p.id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function MonitorCard({ p }: { p: MonitorProject }) {
  const attention = p.attention.needsApproval || p.attention.unreadMessages;
  const flagged = attention || p.failed > 0;

  return (
    <Link
      to={`/console/projects/${p.id}`}
      className={`block rounded-lg bg-white p-4 shadow-sm ring-1 transition hover:shadow ${
        flagged ? 'ring-2 ring-amber-400' : 'ring-secondary/5'
      }`}
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="min-w-0 truncate font-title text-lg tracking-wide">{p.name}</span>
        <span className="ml-auto flex-shrink-0">
          <ProjectStatusBadge status={p.status} />
        </span>
      </div>
      <div className="mb-2 truncate text-xs opacity-50">{p.ownerEmail}</div>

      {flagged ? (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {p.attention.needsApproval ? <Chip tone="amber">Needs approval</Chip> : null}
          {p.attention.unreadMessages ? <Chip tone="amber">Unread messages</Chip> : null}
          {p.failed > 0 ? (
            <Chip tone="red">
              {p.failed} failed build{p.failed === 1 ? '' : 's'}
            </Chip>
          ) : null}
        </div>
      ) : null}

      {p.active.length > 0 ? (
        <ul className="mb-3 flex flex-col gap-1">
          {p.active.map((a) => (
            <li key={a.buildId} className="flex items-center gap-2 text-sm">
              <BuildStatusBadge status={a.status} />
              <span className="min-w-0 truncate">{a.storyTitle}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-sm opacity-50">No builds in flight.</p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs opacity-70">
        <span>
          {p.stories.done}/{p.stories.total} built
          {p.stories.inTest > 0 ? `, ${p.stories.inTest} in test` : ''}
        </span>
        <span className="ml-auto flex-shrink-0">
          <DeployStatusBadge status={p.deployStatus} />
        </span>
      </div>
    </Link>
  );
}

function Chip({ tone, children }: { tone: 'amber' | 'red'; children: ReactNode }) {
  const cls = tone === 'red' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}
